'use client'

import { Button } from '@progress/kendo-react-buttons'
import { House, RefreshCw, TriangleAlert } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'

import { isChunkLoadError, recoverFromChunkError } from '@/common/helpers/chunkErrorRecovery'
import { getAppInsights } from '@/core/providers/ConfigAppInsightsContainer/AppInsights'

interface IErrorProps {
  error: Error & { digest?: string }
  reset: () => void
}

const ErrorBoundary: React.FC<IErrorProps> = ({ error, reset }) => {
  const router = useRouter()
  const [recargando, setRecargando] = useState(false)
  const esFalloDeVersion = isChunkLoadError(error)

  useEffect(() => {
    // Fallo de carga de chunk tras un deploy: el HTML del cliente pide chunks que el contenedor
    // nuevo todavia no sirve. Un reload trae el manifest correcto (guard por intentos en el helper).
    if (esFalloDeVersion && recoverFromChunkError()) {
      setRecargando(true)

      return
    }
    // Registrar el error en Application Insights (si está inicializado) y en consola
    getAppInsights()?.trackException({ exception: error })
    console.error('Error capturado por el error boundary:', error)
  }, [error, esFalloDeVersion])

  if (recargando) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center text-center"
        style={{ minHeight: '70vh', padding: '2rem' }}
      >
        <RefreshCw size={48} className="text-muted" />
        <p className="text-muted mt-3 mb-0">Hay una nueva versión disponible, actualizando…</p>
      </div>
    )
  }

  // Chunk que sigue sin cargar despues de agotar las recargas. Aca `reset()` no sirve: Turbopack
  // cachea la promesa rechazada del chunk, asi que reintentar el render devuelve el mismo fallo.
  // Lo unico que puede resolverlo es traer HTML nuevo, y si dos recargas no alcanzaron es porque el
  // despliegue todavia esta en curso. Por eso el texto dice que espere y reintente, en vez de dejar
  // un boton que no puede funcionar.
  if (esFalloDeVersion) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center text-center"
        style={{ minHeight: '70vh', padding: '2rem' }}
      >
        <RefreshCw size={72} className="text-warning" style={{ opacity: 0.9 }} />
        <h1 className="fw-semibold mt-4 mb-2" style={{ color: '#093f70' }}>
          Se está publicando una nueva versión
        </h1>
        <p className="text-muted mb-4" style={{ maxWidth: 460 }}>
          La aplicación se actualizó mientras la tenías abierta y todavía no termina de publicarse.
          Espera unos segundos y vuelve a cargar. No se pierde nada de lo que ya se guardó.
        </p>
        <Button themeColor="primary" onClick={() => window.location.reload()}>
          <RefreshCw size={16} className="align-middle me-1" style={{ flexShrink: 0 }} />
          Volver a cargar
        </Button>
      </div>
    )
  }

  return (
    <div
      className="d-flex flex-column align-items-center justify-content-center text-center"
      style={{ minHeight: '70vh', padding: '2rem' }}
    >
      <TriangleAlert size={72} className="text-danger" style={{ opacity: 0.9 }} />
      <h1 className="fw-semibold mt-4 mb-2" style={{ color: '#093f70' }}>
        Algo salió mal
      </h1>
      <p className="text-muted mb-4" style={{ maxWidth: 460 }}>
        Ocurrió un error inesperado. Puedes reintentar la operación o volver al inicio. Si el problema persiste,
        contacta al administrador del sistema.
      </p>
      <div className="d-flex flex-wrap justify-content-center gap-2">
        <Button themeColor="primary" onClick={() => reset()}>
          <RefreshCw size={16} className="align-middle me-1" style={{ flexShrink: 0 }} />
          Reintentar
        </Button>
        <Button themeColor="base" onClick={() => router.push('/')}>
          <House size={16} className="align-middle me-1" style={{ flexShrink: 0 }} />
          Ir al inicio
        </Button>
      </div>
    </div>
  )
}

export default ErrorBoundary
